/**
 * ResponsiveTable - Tabella adattiva per mobile e desktop
 */

import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

interface ResponsiveTableProps<T> {
  data: T[];
  columns: {
    id: keyof T | 'actions';
    label: string;
    render?: (row: T) => React.ReactNode;
    align?: 'left' | 'center' | 'right';
    width?: string;
    mobileHidden?: boolean;
  }[];
  mobileView?: (row: T) => React.ReactNode;
  loading?: boolean;
  noResultsMessage?: string;
  maxHeight?: string;
  stickyHeader?: boolean;
}

const ResponsiveTable = <T extends { id: string | number }>({
  data,
  columns,
  mobileView,
  loading = false,
  noResultsMessage = 'Nessun risultato trovato.',
  maxHeight = '70vh',
  stickyHeader = false,
}: ResponsiveTableProps<T>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());

  const handleExpandClick = (rowId: string | number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  if (loading) {
    return (
      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: 2,
          maxHeight: maxHeight,
          overflow: 'auto'
        }}
      >
        <Table stickyHeader={stickyHeader}>
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Caricamento dati...
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (data.length === 0) {
    return (
      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: 2,
          maxHeight: maxHeight,
          overflow: 'auto'
        }}
      >
        <Table stickyHeader={stickyHeader}>
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {noResultsMessage}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Vista mobile con card
  if (isMobile && mobileView) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.map((row) => (
          <Paper
            key={String(row.id)}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {mobileView(row)}
          </Paper>
        ))}
      </Box>
    );
  }

  // Vista desktop con tabella
  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        borderRadius: 2,
        maxHeight: maxHeight,
        overflow: 'auto'
      }}
    >
      <Table stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {columns.map((column, colIndex) => (
              <TableCell
                key={String(column.id) || `col-${colIndex}`}
                align={column.align || 'left'}
                sx={{
                  fontWeight: 'bold',
                  width: column.width,
                  display: isMobile && column.mobileHidden ? 'none' : 'table-cell',
                  ...(stickyHeader && {
                    backgroundColor: 'background.paper',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }),
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <React.Fragment key={row.id || `row-${index}`}>
              <TableRow hover>
                {columns.map((column, colIndex) => (
                  <TableCell
                    key={String(column.id) || `cell-${colIndex}`}
                    align={column.align || 'left'}
                    sx={{
                      display: isMobile && column.mobileHidden ? 'none' : 'table-cell',
                    }}
                  >
                    {column.render ? column.render(row) : String(row[column.id as keyof T])}
                  </TableCell>
                ))}
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ResponsiveTable;
